import React, { useState, useMemo, useEffect } from 'react';
import { FiChevronUp, FiChevronDown, FiPlus, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './DataTable.css';

/**
 * Badge  para celdas de estado.
 * Variantes: success | danger | warning | info | purple | orange | default
 */
export const Badge = ({ variant, children }) => (
  <span className={'dt-badge dt-badge-' + (variant || 'default')}>{children}</span>
);

/**
 * Botón de acción para celdas (estilo estándar de las tablas).
 *  href      Si viene, se dibuja como enlace
 *  square    Botón cuadrado para íconos
 *  variant   'danger' resalta en rojo al pasar el mouse
 *  loading   Muestra un spinner en lugar del contenido y desactiva el botón
 * El resto de props (onClick, disabled, aria-label…) pasan al elemento; acepta ref para tooltips.
 */
export const ActionButton = React.forwardRef(({ href, square, variant, loading, disabled, className, children, ...rest }, ref) => {
  const clase = 'dt-btn-edit' + (square ? ' dt-btn-square' : '') + (variant ? ' dt-btn-edit-' + variant : '') + (className ? ' ' + className : '');
  const contenido = loading ? <span className="dt-spinner" aria-hidden="true" /> : children;
  return href
    ? <a ref={ref} href={href} className={clase} {...rest}>{contenido}</a>
    : <button ref={ref} type="button" className={clase} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>{contenido}</button>;
});

/**
 * Props:
 *  title               Título fuera de la tarjeta
 *  columns             [{ key, label, sortable?, render?(value, row) }]
 *  data                Array de objetos
 *  searchKeys          Campos del objeto donde buscar
 *  searchPlaceholder   Placeholder del input
 *  createUrl           URL botón Crear
 *  createLabel         Texto botón Crear
 *  editUrl             URL base del botón Editar
 *  editKey             Clave ID del registro para url editar
 *  editLabel           Texto botón Editar (default: 'Editar')
 *  loading             
 *  emptyText           
 *  defaultItemsPerPage Items por página
 */
const DataTable = ({
  title,
  columns,
  data,
  searchKeys,
  searchPlaceholder,
  createUrl,
  createLabel,
  editUrl,
  editKey,
  editLabel,
  loading,
  emptyText,
  defaultItemsPerPage,
  renderFilters,
  topRightContent,
  onFilteredDataChange,
  rowClassName,
}) => {
  columns = columns || [];
  data = data || [];
  searchKeys = searchKeys || [];
  searchPlaceholder = searchPlaceholder || 'Buscar...';
  createLabel = createLabel || 'Crear';
  editLabel = editLabel || 'Editar';
  loading = loading || false;
  emptyText = emptyText || 'Sin registros';
  defaultItemsPerPage = defaultItemsPerPage || 10;

  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handlePageChange = (newPage) => {
    if (newPage === currentPage) return;
    setIsTransitioning(true);
    setCurrentPage(newPage);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 450);
  };

  var handleSort = function (col) {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir(function (d) { return d === 'asc' ? 'desc' : 'asc'; });
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
    handlePageChange(1);
  };

  // Debounce the quick search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(inputValue);
    }, 250);
    return () => clearTimeout(handler);
  }, [inputValue]);

  var handleSearch = function (e) {
    setInputValue(e.target.value);
    handlePageChange(1);
  };

  var filteredRows = useMemo(function () {
    var q = query.toLowerCase().trim();

    var searchValuesMap = {};
    if (columns) {
      columns.forEach(function (c) {
        if (typeof c.searchValue === 'function') {
          searchValuesMap[c.key] = c.searchValue;
        }
      });
    }

    var result = (q && searchKeys.length)
      ? data.filter(function (row) {
        return searchKeys.some(function (k) {
          var val = row[k];
          var searchStr = val;
          if (searchValuesMap[k]) {
            searchStr = searchValuesMap[k](val, row);
          }
          return searchStr !== undefined && searchStr !== null && String(searchStr).toLowerCase().indexOf(q) > -1;
        });
      })
      : data;

    if (sortKey) {
      result = result.slice().sort(function (a, b) {
        var av = a[sortKey] != null ? a[sortKey] : '';
        var bv = b[sortKey] != null ? b[sortKey] : '';
        var cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [data, query, searchKeys, sortKey, sortDir, columns]);

  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredRows);
    }
  }, [filteredRows, onFilteredDataChange]);

  /* ---- Paginacion ---- */
  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / itemsPerPage) || 1;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = Math.min(startIdx + itemsPerPage, totalRows);
  const pageRows = filteredRows.slice(startIdx, endIdx);

  /* Números de pagina visibles */
  var pageNumbers = useMemo(function () {
    var delta = 2;
    var pages = [];
    var from = Math.max(1, currentPage - delta);
    var to = Math.min(totalPages, currentPage + delta);
    for (var i = from; i <= to; i++) pages.push(i);
    while (pages.length < Math.min(6, totalPages)) {
      if (pages[0] > 1) {
        pages.unshift(pages[0] - 1);
      } else if (pages[pages.length - 1] < totalPages) {
        pages.push(pages[pages.length - 1] + 1);
      } else {
        break;
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  /* ---- Columna de acciones ---- */
  var allColumns = useMemo(function () {
    if (!editUrl || !editKey) return columns;
    return columns.concat([{
      key: '__edit__',
      label: 'Acciones',
      render: function (_, row) {
        return <ActionButton href={editUrl + '?' + row[editKey]}>{editLabel}</ActionButton>;
      },
    }]);
  }, [columns, editUrl, editKey, editLabel]);

  /* ---- icono ordenamiento ---- */
  var SortIcon = function (props) {
    if (!props.sortable) return null;
    if (sortKey !== props.colKey) return <span className="dt-sort-icon">&#8597;</span>;
    return sortDir === 'asc'
      ? <FiChevronUp className="dt-sort-icon active" />
      : <FiChevronDown className="dt-sort-icon active" />;
  };

  return (
    <div className="dt-outer">

      {/* Encabezado de página título, botón crear */}
      {(title || createUrl) && (
        <div className="dt-page-header">
          {title && <h1 className="dt-page-title">{title}</h1>}
          {createUrl && (
            <a href={createUrl} className="dt-btn-create">
              <FiPlus size={14} />
              {createLabel}
            </a>
          )}
        </div>
      )}

      <div className="dt-wrapper">

        {/* Filtros configurables */}
        {renderFilters && (
          <div className="dt-filters-bar">
            {renderFilters}
          </div>
        )}

        {/* Controles Superiores: Búsqueda, Paginación Superior y Filtro Derecho */}
        <div className="dt-top-controls d-flex justify-content-between align-items-center mb-1">
          <div className="dt-top-search" style={{ flex: '1' }}>
            {searchKeys.length > 0 && (
              <div className="dt-search-bar" style={{ marginBottom: 0 }}>
                <FiSearch className="dt-search-icon" size={18} />
                <input
                  className="dt-search"
                  type="text"
                  placeholder={searchPlaceholder}
                  value={inputValue}
                  onChange={handleSearch}
                />
              </div>
            )}
          </div>

          <div className="dt-top-pagination" style={{
            flex: '1',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}>
            {totalPages > 1 && (
              <div className="dt-pagination">
                <button
                  className="dt-page-btn dt-page-arrow"
                  onClick={function () { handlePageChange(Math.max(1, currentPage - 1)); }}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft size={13} />
                </button>
                {pageNumbers.map(function (n) {
                  return (
                    <button
                      key={n}
                      className={'dt-page-btn' + (currentPage === n ? ' active' : '')}
                      onClick={function () { handlePageChange(n); }}
                    >
                      {n}
                    </button>
                  );
                })}
                <button
                  className="dt-page-btn dt-page-arrow"
                  onClick={function () { handlePageChange(Math.min(totalPages, currentPage + 1)); }}
                  disabled={currentPage === totalPages}
                >
                  <FiChevronRight size={13} />
                </button>
              </div>
            )}
            {totalRows > 0 && (
              <span className="dt-results-text" style={{ fontSize: '12px', color: '#6e7581ff', fontWeight: '500', whiteSpace: 'nowrap' }}>
                {'Mostrando ' + (startIdx + 1) + ' - ' + endIdx + ' de ' + totalRows}
              </span>
            )}
          </div>

          <div className="dt-top-right-content" style={{ flex: '1', display: 'flex', justifyContent: 'flex-end', paddingRight: '15px' }}>
            {topRightContent}
          </div>
        </div>

        {/* Tabla */}
        <div className="dt-table-container">
          <table className="dt-table">
            <thead>
              <tr>
                {allColumns.map(function (col) {
                  return (
                    <th
                      key={col.key}
                      className={col.sortable ? 'dt-th-sortable' : ''}
                      onClick={function () { handleSort(col); }}
                      style={col.style}
                    >
                      <span className="dt-th-inner">
                        {col.label}
                        <SortIcon colKey={col.key} sortable={col.sortable} />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(loading || isTransitioning) ? (
                Array.from({ length: 8 }).map(function (_, rowIndex) {
                  return (
                    <tr key={"skel-" + rowIndex} className="dt-skeleton-row">
                      {allColumns.map(function (col, colIndex) {
                        var widths = ['55%', '80%', '35%', '70%', '45%', '90%', '60%', '40%'];
                        var w = widths[(rowIndex + colIndex) % widths.length];
                        return (
                          <td key={col.key}>
                            <span className="dt-skeleton-block" style={{ width: w }}></span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={allColumns.length} className="dt-empty">{emptyText}</td></tr>
              ) : (
                pageRows.map(function (row, i) {
                  return (
                    <tr key={currentPage + "-" + i} className={"dt-row-appear" + (rowClassName && rowClassName(row) ? " " + rowClassName(row) : "")} style={{ animationDelay: Math.min(i * 50, 400) + 'ms' }}>
                      {allColumns.map(function (col) {
                        return (
                          <td key={col.key} style={col.style}>
                            {col.render
                              ? col.render(row[col.key], row)
                              : (row[col.key] != null ? row[col.key] : '—')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* contador, paginación, items por página */}
        <div className="dt-footer" style={{
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: loading ? 'none' : 'auto'
        }}>
          <div style={{ flex: '1', display: 'flex', justifyContent: 'flex-start', paddingLeft: '15px' }}>
            {totalRows > 0 && (
              <span className="dt-results-text" style={{ fontSize: '13px', color: '#374151' }}>
                {'Mostrando ' + (startIdx + 1) + ' - ' + endIdx + ' de ' + totalRows + ' registros'}
                {query ? ' para "' + query + '"' : ''}
              </span>
            )}
          </div>

          <div style={{ flex: '1', display: 'flex', justifyContent: 'center' }}>
            {totalPages > 1 && (
              <div className="dt-pagination">
                <button
                  className="dt-page-btn dt-page-arrow"
                  onClick={function () { handlePageChange(Math.max(1, currentPage - 1)); }}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft size={13} />
                </button>

                {pageNumbers.map(function (n) {
                  return (
                    <button
                      key={n}
                      className={'dt-page-btn' + (currentPage === n ? ' active' : '')}
                      onClick={function () { handlePageChange(n); }}
                    >
                      {n}
                    </button>
                  );
                })}

                <button
                  className="dt-page-btn dt-page-arrow"
                  onClick={function () { handlePageChange(Math.min(totalPages, currentPage + 1)); }}
                  disabled={currentPage === totalPages}
                >
                  <FiChevronRight size={13} />
                </button>
              </div>
            )}
          </div>

          <div className="dt-per-page-wrap" style={{ flex: '1', display: 'flex', justifyContent: 'flex-end' }}>
            <span className="dt-per-page-label">Items por p&#225;gina</span>
            <select
              className="dt-per-page"
              value={itemsPerPage}
              onChange={function (e) {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 20, 50, 100].map(function (n) {
                return <option key={n} value={n}>{n}</option>;
              })}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;



// WEBPACK FOOTER //
// ./src/components/widgets/DataTable.jsx